// 붙여넣기 필터 (2026-09-26).
//
// 출처에 따라 허용 범위를 나눈다.
//   워드·한글  → 정해진 서식만 남기고 나머지는 버린다
//   그 밖의 HTML → 텍스트만
//   우리 에디터에서 복사한 것 → 그대로
//
// 🔴 **들어온 뒤 지우는 게 아니라 들어오기 전에 거른다.** 예전에는 onEditorChange 가
//    키 입력마다 전체 줄을 돌며 색을 지우고 `root.innerHTML` 을 정규식으로 되썼다.
//    퀼 1.x 의 Scroll 은 MutationObserver 로 DOM 을 감시하다 다시 파싱하기 때문에,
//    innerHTML 되쓰기는 Delta·실행취소 이력·커서 위치를 어긋나게 만든다.
//
// 퀼이 제공하는 확장점(clipboard matcher)만 쓴다. 내부 API 에 손대지 않는다.
import Quill from 'quill'

const Delta = Quill.import('delta')

// 🔴 퀼 2 에서 clipboard.convert 의 시그니처가 바뀌었다.
//    퀼 1: convert(html)   퀼 2: convert({ html })
//    두 버전에서 다 돌게 감싼다.
const QUILL_MAJOR = parseInt(String(Quill.version || '1').split('.')[0], 10) || 1
function convertHtml(quill, html) {
    return QUILL_MAJOR >= 2 ? quill.clipboard.convert({ html: html })
                            : quill.clipboard.convert(html)
}

// 워드·한글에서 살릴 서식. 여기 없는 것은 전부 버린다
// (색·배경·폰트·크기·자간·들여쓰기·mso-* 등).
// 🔴 **툴바로 직접 넣은 서식에는 영향이 없다.** 이 목록은 붙여넣기 변환에만 쓴다.
export const WORD_ALLOW = [
    'bold', 'italic', 'underline', 'strike',
    'highlight',          // 우리 커스텀 포맷(Parchment ClassAttributor)
    'link', 'list', 'align',
]

// 제목·부제처럼 한 줄짜리 입력칸에서 살릴 서식.
export const TITLE_ALLOW = ['italic', 'underline', 'strike', 'highlight']

// 워드는 Generator 메타·xmlns·MsoNormal·mso-* 스타일을 남긴다.
const WORD_SIGN = /(urn:schemas-microsoft-com:office|class=["']?Mso|mso-[a-z-]+\s*:|content=["']?Microsoft Word)/i
// 한글(HWP)은 Hancom/HWP 표식이나 HStyle 클래스를 남긴다.
const HWP_SIGN = /(content=["']?(Hancom|HWP)|hwpEditorData|class=["']?HStyle|<html[^>]+xmlns:o=)/i
// 우리 에디터에서 복사하면 퀼 클래스가 딸려 온다.
const SELF_SIGN = /class=["'][^"']*\bql-/i

function hasType(e, t) {
    try {
        const types = (e.clipboardData && e.clipboardData.types) || []
        return Array.prototype.indexOf.call(types, t) >= 0
    } catch (err) { return false }
}

export function detectSource(html) {
    if (!html) return 'text'                 // text/html 이 없으면 원래 평문이다
    if (SELF_SIGN.test(html)) return 'self'
    if (WORD_SIGN.test(html)) return 'word'
    if (HWP_SIGN.test(html)) return 'word'   // 한글도 같은 정책을 쓴다
    if (/<[a-z][\s\S]*>/i.test(html)) return 'external'
    return 'text'
}

// 표는 퀼 1.x 가 지원하지 않는다. 셀은 공백, 행은 줄바꿈으로 풀어 글자만 남긴다.
function flattenTable(node) {
    const rows = []
    node.querySelectorAll('tr').forEach(tr => {
        const cells = []
        tr.querySelectorAll('th,td').forEach(td => {
            const t = (td.innerText || td.textContent || '').replace(/\s+/g, ' ').trim()
            if (t) cells.push(t)
        })
        if (cells.length) rows.push(cells.join(' '))
    })
    return rows.join('\n')
}

// 클립보드 HTML 을 평문으로. 줄 구조만 살린다.
function htmlToPlain(html) {
    const div = document.createElement('div')
    div.innerHTML = String(html || '')
    div.querySelectorAll('script,style,head,meta,link').forEach(n => n.remove())
    div.querySelectorAll('table').forEach(t => {
        const p = document.createElement('div')
        p.textContent = flattenTable(t)
        t.parentNode.replaceChild(p, t)
    })
    div.querySelectorAll('br').forEach(b => b.parentNode.replaceChild(document.createTextNode('\n'), b))
    div.querySelectorAll('p,div,li,tr,h1,h2,h3,h4,h5,h6').forEach(b => b.appendChild(document.createTextNode('\n')))
    return (div.innerText || div.textContent || '')
        .replace(/\u00a0/g, ' ')      // &nbsp; → 보통 공백
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+\n/g, '\n')
        .trim()
}

// 선택 영역을 평문으로 갈아 끼운다. 서식은 붙지 않는다.
function insertPlain(quill, text) {
    if (!text) return
    const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 }
    const delta = new Delta().retain(range.index).delete(range.length).insert(text)
    quill.updateContents(delta, 'user')
    quill.setSelection(range.index + text.length, 0, 'silent')
}


// ── 붙여넣기 속 이미지 ────────────────────────────────────────────────────
// data: URI 로 들어온 것만 살린다. 업로드해서 우리 주소로 바꿔 끼운다.
//
// 🔴 **file:/// 은 방법이 없다.** 워드가 HTML 에 넣는 `file:///C:/…/clip_image001.png`
//    은 브라우저가 웹 페이지에서 읽지 못한다(스킴 차단). 워드 이미지를 살리려면
//    클립보드의 text/rtf 안 \pict 에서 바이트를 꺼내야 하는데, 그건 별도 작업이다.
// 🔴 외부 http(s) 이미지도 살리지 않는다. 남의 서버를 그대로 거는 것이라
//    언제 깨질지 모르고, 저작권 문제도 생긴다.
function dataUrlToBlob(dataUrl) {
    const m = /^data:([^;,]+)(;base64)?,(.*)$/i.exec(dataUrl || '')
    if (!m) return null
    const mime = m[1]
    let bytes
    try {
        if (m[2]) {
            const bin = atob(m[3])
            bytes = new Uint8Array(bin.length)
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        } else {
            bytes = new TextEncoder().encode(decodeURIComponent(m[3]))
        }
    } catch (e) { return null }
    return new Blob([bytes], { type: mime })
}

const EXT_BY_MIME = {
    'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg',
    'image/gif': 'gif', 'image/webp': 'webp', 'image/bmp': 'bmp',
}

// html 안의 data: 이미지를 전부 업로드하고 src 를 바꿔 끼운 html 을 돌려준다.
// 업로드에 실패한 것은 태그째 지운다 — 깨진 이미지가 기사에 남는 것보다 낫다.
async function rewriteDataImages(html, uploadImage, allowedSrc) {
    const doc = document.createElement('div')
    doc.innerHTML = html
    // 🔴 워드 HTML 은 대부분이 스타일시트다(실측 164KB 중 거의 전부).
    //    matcher 도 STYLE 을 버리지만, 변환에 태우기 전에 털어내는 편이 싸다.
    doc.querySelectorAll('style,link,meta,title,xml').forEach(function(n) { n.remove() })
    const imgs = Array.prototype.slice.call(doc.querySelectorAll('img'))
    let failed = 0
    for (let i = 0; i < imgs.length; i++) {
        const src = imgs[i].getAttribute('src') || ''
        if (src.slice(0, 5).toLowerCase() !== 'data:') { imgs[i].remove(); continue }
        const blob = dataUrlToBlob(src)
        if (!blob) { imgs[i].remove(); failed++; continue }
        // 서버가 허용하는 확장자가 아니면 sharp 가 JPG 로 바꿔 준다(writeArticles.js /upload).
        const ext = EXT_BY_MIME[blob.type] || 'png'
        let url = ''
        try { url = await uploadImage(blob, 'paste_' + Date.now() + '_' + i + '.' + ext) } catch (e) { url = '' }
        if (!url) { imgs[i].remove(); failed++; continue }
        imgs[i].setAttribute('src', url)
        allowedSrc.add(url)
    }
    return { html: doc.innerHTML, total: imgs.length, failed: failed }
}

/**
 * @param {Quill}    quill
 * @param {Object}   opts
 * @param {string[]} opts.allow   워드·한글에서 살릴 서식 (기본 WORD_ALLOW)
 * @param {Function} opts.notify  안내 문구 콜백 (msg) => void
 * @param {Function} opts.uploadImage  (blob, name) => Promise<url>. 주면 data: 이미지를 살린다
 */
export function installPasteFilter(quill, opts) {
    opts = opts || {}
    const allow = opts.allow || WORD_ALLOW
    const notify = typeof opts.notify === 'function' ? opts.notify : function() {}
    const uploadImage = typeof opts.uploadImage === 'function' ? opts.uploadImage : null
    // 업로드를 마쳐 우리 주소가 된 이미지만 matcher 가 통과시킨다.
    const allowedSrc = new Set()

    // 이 붙여넣기의 출처. matcher 가 읽는다.
    let mode = 'self'

    // 🔴 **모드가 다음 변환으로 새면 안 된다.** 기사를 불러올 때도
    //    vue-quill-editor 가 `quill.pasteHTML(값)` 을 부르고, 그건 결국
    //    clipboard.convert() → 같은 matcher 를 탄다. 워드 붙여넣기 뒤에 모드가
    //    'word' 로 남아 있으면 **저장된 기사를 다시 열 때 서식이 깎인다.**
    //    matcher 는 변환 도중에 돌고 text-change 는 변환이 끝난 뒤에 오므로,
    //    여기서 되돌리면 정확히 한 번의 붙여넣기에만 적용된다.
    quill.on('text-change', function() { mode = 'self' })

    // 🔴 **capture 단계**로 등록한다. 퀼이 생성자에서 먼저 bubble 로 붙였기 때문에,
    //    같은 단계로 붙이면 우리 쪽이 나중에 돈다. 출처는 퀼이 변환을 시작하기 전에
    //    정해져 있어야 한다.
    quill.root.addEventListener('paste', function(e) {
        let html = ''
        let text = ''
        try {
            html = (e.clipboardData && e.clipboardData.getData('text/html')) || ''
            text = (e.clipboardData && e.clipboardData.getData('text/plain')) || ''
        } catch (err) { /* 클립보드 접근이 막힌 환경 */ }

        // 이미지 파일만 담긴 붙여넣기는 기존 업로드 경로가 처리한다. 건드리지 않는다.
        const items = (e.clipboardData && e.clipboardData.items) || []
        for (let i = 0; i < items.length; i++) {
            if (items[i].type && items[i].type.indexOf('image') !== -1) { mode = 'self'; return }
        }

        mode = detectSource(html)

        // 워드·한글 안에 data: 이미지가 있으면 업로드해서 살린다.
        // 🔴 업로드는 비동기인데 붙여넣기는 동기다. 그래서 퀼에 맡기지 않고
        //    preventDefault 로 막은 뒤 변환까지 우리가 한다. 이때 mode 는 아직
        //    'word' 다 — text-change 가 아직 안 왔기 때문이다.
        if (mode === 'word' && uploadImage && /<img[^>]+src=["']?data:image/i.test(html)) {
            e.preventDefault()
            const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 }
            rewriteDataImages(html, uploadImage, allowedSrc).then(function(res) {
                const delta = convertHtml(quill, res.html)
                quill.updateContents(
                    new Delta().retain(range.index).delete(range.length).concat(delta), 'user')
                quill.setSelection(range.index + delta.length(), 0, 'silent')
                if (res.failed) notify(res.failed + '장은 불러오지 못해 빠졌습니다.')
            }).catch(function() {
                // 업로드 경로가 통째로 실패하면 글자라도 넣는다.
                insertPlain(quill, text || htmlToPlain(html))
                notify('이미지를 불러오지 못해 글자만 붙였습니다.')
            })
            return
        }

        if (mode === 'external' || mode === 'text') {
            // 🔴 퀼의 onPaste 는 `e.defaultPrevented` 면 통째로 빠져나간다.
            //    그래서 여기서 막고 우리가 직접 넣으면 HTML 변환이 아예 일어나지 않는다.
            e.preventDefault()
            const plain = text || htmlToPlain(html)
            insertPlain(quill, plain)
            if (mode === 'external') notify('외부 문서라 서식 없이 글자만 붙였습니다.')
            else if (!html && hasType(e, 'text/rtf')) {
                // 워드프로세서에서 붙였는데 HTML 이 안 왔다. 한글이 이렇게 준다
                // (types 에 text/html 이 있어도 getData 는 빈 문자열이다).
                // 🔴 **이미지가 몇 장인지는 세지 않는다.** 세려면 text/rtf 를 문자열로
                //    읽어야 하는데 실측에서 **68MB** 가 나왔다. clipboardData 는
                //    이벤트 처리 중에만 유효해서 뒤로 미룰 수도 없다 — 읽는 순간 멈춘다.
                notify('문서에서 붙여넣었습니다. 이미지가 있었다면 따로 올려주세요.')
            }
        }
    }, true)

    // 워드·한글: 허용 목록에 없는 속성을 변환 과정에서 떨어뜨린다.
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, function(node, delta) {
        if (mode !== 'word') return delta

        // 표는 글자만 남긴다.
        if (node.tagName === 'TABLE') {
            const t = flattenTable(node)
            return t ? new Delta().insert(t + '\n') : new Delta()
        }
        // 워드가 남기는 껍데기. 빈 Delta 를 주면 그 가지가 통째로 빠진다.
        if (node.tagName === 'STYLE' || node.tagName === 'SCRIPT' ||
            node.tagName === 'META' || node.tagName === 'LINK') {
            return new Delta()
        }

        delta.ops = (delta.ops || []).map(function(op) {
            // 워드는 빈칸을 &nbsp; 로 채워 보낸다. 퀼의 matchText 가 \u00a0 을
            // **일부러 살려 두기 때문에**(공백 축약에서 제외한다) 여기서 정리한다.
            // 그대로 두면 본문에 &nbsp; 가 줄줄이 저장된다.
            if (typeof op.insert === 'string' && op.insert.indexOf('\u00a0') >= 0) {
                op = Object.assign({}, op, { insert: op.insert.replace(/\u00a0+/g, ' ') })
            }
            // 이미지는 버린다. file:// 은 브라우저가 못 읽고, 외부 http 는
            // 남의 서버를 그대로 거는 것이라 언제 깨질지 모른다.
            if (op.insert && typeof op.insert === 'object' && op.insert.image) {
                // 업로드를 마친 우리 주소만 남긴다. file:// 과 외부 http 는 버린다.
                return allowedSrc.has(op.insert.image) ? op : null
            }
            if (!op.attributes) return op
            const kept = {}
            let dropped = false
            Object.keys(op.attributes).forEach(function(k) {
                if (allow.indexOf(k) >= 0) kept[k] = op.attributes[k]
                else dropped = true
            })
            if (!dropped) return op
            const next = { insert: op.insert }
            if (Object.keys(kept).length) next.attributes = kept
            return next
        }).filter(Boolean)

        return delta
    })

    // 바깥에서 현재 모드를 볼 수 있게 (테스트용)
    return { getMode: function() { return mode } }
}

export default installPasteFilter
